import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const StoresOperations = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [inventory, setInventory] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [movements, setMovements] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showPOModal, setShowPOModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showPODetails, setShowPODetails] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poItems, setPOItems] = useState([]);
  const [movementForm, setMovementForm] = useState({
    item_id: '',
    movement_type: 'out',
    quantity: '',
    notes: '',
    created_by: 'Admin'
  });
  const [poForm, setPOForm] = useState({
    supplier_id: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery: '',
    notes: '',
    created_by: 'Admin',
    items: []
  });
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    low: '#ef4444',
    reorder: '#f59e0b',
    good: '#4a7c2a'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchInventory(),
      fetchLowStock(),
      fetchPurchaseOrders(),
      fetchMovements(),
      fetchSuppliers()
    ]);
    setLoading(false);
  };

  const fetchInventory = async () => {
    try {
      const data = await apiGet('/admin/stores/operations/inventory');
      if (data.success) setInventory(data.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const fetchLowStock = async () => {
    try {
      const data = await apiGet('/admin/stores/operations/low-stock');
      if (data.success) setLowStock(data.data);
    } catch (error) {
      console.error('Error fetching low stock:', error);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const data = await apiGet('/admin/stores/operations/purchase-orders');
      if (data.success) setPurchaseOrders(data.data);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
    }
  };

  const fetchMovements = async () => {
    try {
      const data = await apiGet('/admin/stores/operations/movements');
      if (data.success) setMovements(data.data);
    } catch (error) {
      console.error('Error fetching movements:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await apiGet('/admin/stores/setup/suppliers');
      if (data.success) setSuppliers(data.data.filter(s => s.is_active));
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchPODetails = async (id) => {
    try {
      const data = await apiGet('/admin/stores/operations/purchase-orders/${id}');
      if (data.success) {
        setSelectedPO(data.data.order);
        setPOItems(data.data.items);
        setShowPODetails(true);
      }
    } catch (error) {
      console.error('Error fetching PO details:', error);
    }
  };

  const handleRecordMovement = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/stores/operations/stock-movement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(movementForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Stock movement recorded successfully!');
        setShowMovementModal(false);
        setMovementForm({
          item_id: '',
          movement_type: 'out',
          quantity: '',
          notes: '',
          created_by: 'Admin'
        });
        fetchAllData();
      }
    } catch (error) {
      console.error('Error recording movement:', error);
    }
  };

  const updatePOStatus = async (id, status) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/stores/operations/purchase-orders/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Purchase order ${status}!`);
        fetchAllData();
        if (showPODetails) {
          setShowPODetails(false);
        }
      }
    } catch (error) {
      console.error('Error updating PO status:', error);
    }
  };

  const addPOItem = () => {
    setPOForm({
      ...poForm,
      items: [...poForm.items, { item_id: '', quantity: '', unit_price: '', total_price: 0 }]
    });
  };

  const removePOItem = (index) => {
    setPOForm({
      ...poForm,
      items: poForm.items.filter((_, i) => i !== index)
    });
  };

  const updatePOItem = (index, field, value) => {
    const updatedItems = [...poForm.items];
    updatedItems[index][field] = value;

    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(updatedItems[index].quantity) || 0;
      const price = parseFloat(updatedItems[index].unit_price) || 0;
      updatedItems[index].total_price = (qty * price).toFixed(2);
    }

    setPOForm({ ...poForm, items: updatedItems });
  };

  const handleCreatePO = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/stores/operations/purchase-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(poForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Purchase order ${data.data.po_number} created successfully!`);
        setShowPOModal(false);
        setPOForm({
          supplier_id: '',
          order_date: new Date().toISOString().split('T')[0],
          expected_delivery: '',
          notes: '',
          created_by: 'Admin',
          items: []
        });
        fetchAllData();
      }
    } catch (error) {
      console.error('Error creating PO:', error);
    }
  };

  const getStockColor = (status) => {
    return colors[status] || colors.textLight;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'space-between'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: colors.primary,
      color: colors.white
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '700px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '12px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      background: colors.white
    },
    actionButton: {
      padding: '6px 12px',
      borderRadius: '6px',
      border: 'none',
      fontSize: '11px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginRight: '6px'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading stores operations...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 20px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          background: 'rgba(74, 124, 42, 0.15)',
          color: colors.success,
          border: `2px solid ${colors.success}`,
          margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
        }}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Stores Operations</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Inventory management' : 'Manage inventory, stock levels, and procurement operations'}
        </p>
      </div>

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <div style={{ ...styles.contentCard, borderLeft: `4px solid ${colors.danger}` }}>
          <h2 style={styles.sectionTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>⚠️</span>
              <span>Low Stock Alert ({lowStock.length} items)</span>
            </div>
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {lowStock.slice(0, 5).map(item => (
              <div key={item.id} style={{
                padding: '10px',
                background: colors.lightGray,
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong style={{ fontSize: '13px' }}>{item.item_name}</strong>
                  <span style={{ fontSize: '11px', color: colors.textLight, marginLeft: '8px' }}>
                    ({item.item_code})
                  </span>
                </div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: colors.danger }}>
                  {item.current_stock} {item.unit}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '12px' : '16px', marginBottom: isMobile ? '16px' : '24px', padding: isMobile ? '0 4px' : '0' }}>
        <button
          onClick={() => setShowPOModal(true)}
          style={{ ...styles.button, padding: isMobile ? '14px' : '16px' }}
        >
          📝 New Purchase Order
        </button>
        <button
          onClick={() => setShowMovementModal(true)}
          style={{ ...styles.button, background: colors.accent, padding: isMobile ? '14px' : '16px' }}
        >
          📦 Record Stock Movement
        </button>
      </div>

      {/* Inventory Table */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📦</span>
            <span>Current Inventory</span>
          </div>
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Item Code</th>
                <th style={styles.tableHeader}>Item Name</th>
                {!isMobile && <th style={styles.tableHeader}>Category</th>}
                <th style={styles.tableHeader}>Stock</th>
                <th style={styles.tableHeader}>Unit</th>
                {!isMobile && <th style={styles.tableHeader}>Value</th>}
                <th style={styles.tableHeader}>Status</th>
              </tr>
            </thead>
            <tbody>
              {inventory.map(item => (
                <tr key={item.id}>
                  <td style={styles.tableCell}><strong>{item.item_code}</strong></td>
                  <td style={styles.tableCell}>{item.item_name}</td>
                  {!isMobile && (
                    <td style={{ ...styles.tableCell, textTransform: 'capitalize' }}>
                      {item.category}
                    </td>
                  )}
                  <td style={styles.tableCell}>
                    <strong style={{ color: getStockColor(item.stock_status) }}>
                      {item.current_stock}
                    </strong>
                  </td>
                  <td style={styles.tableCell}>{item.unit}</td>
                  {!isMobile && (
                    <td style={styles.tableCell}>
                      ₹{(item.current_stock * item.unit_price).toLocaleString('en-IN')}
                    </td>
                  )}
                  <td style={styles.tableCell}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: `${getStockColor(item.stock_status)}20`,
                      color: getStockColor(item.stock_status),
                      textTransform: 'capitalize'
                    }}>
                      {item.stock_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Purchase Orders */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📋</span>
            <span>Purchase Orders</span>
          </div>
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>PO Number</th>
                <th style={styles.tableHeader}>Supplier</th>
                {!isMobile && <th style={styles.tableHeader}>Order Date</th>}
                <th style={styles.tableHeader}>Amount</th>
                <th style={styles.tableHeader}>Status</th>
                <th style={styles.tableHeader}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {purchaseOrders.map(po => (
                <tr key={po.id}>
                  <td style={styles.tableCell}><strong>{po.po_number}</strong></td>
                  <td style={styles.tableCell}>{po.supplier_name}</td>
                  {!isMobile && (
                    <td style={styles.tableCell}>{formatDate(po.order_date)}</td>
                  )}
                  <td style={styles.tableCell}>
                    <strong>₹{parseFloat(po.total_amount).toLocaleString('en-IN')}</strong>
                  </td>
                  <td style={styles.tableCell}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: po.status === 'received' ? `${colors.success}20` :
                        po.status === 'approved' ? `${colors.accent}20` : `${colors.warning}20`,
                      color: po.status === 'received' ? colors.success :
                        po.status === 'approved' ? colors.accent : colors.warning,
                      textTransform: 'capitalize'
                    }}>
                      {po.status}
                    </span>
                  </td>
                  <td style={styles.tableCell}>
                    <button
                      onClick={() => fetchPODetails(po.id)}
                      style={{
                        ...styles.actionButton,
                        background: colors.accent,
                        color: colors.white
                      }}
                    >
                      👁️ View
                    </button>
                    {po.status === 'pending' && (
                      <button
                        onClick={() => updatePOStatus(po.id, 'approved')}
                        style={{
                          ...styles.actionButton,
                          background: colors.success,
                          color: colors.white
                        }}
                      >
                        ✓ Approve
                      </button>
                    )}
                    {po.status === 'approved' && (
                      <button
                        onClick={() => updatePOStatus(po.id, 'received')}
                        style={{
                          ...styles.actionButton,
                          background: colors.primary,
                          color: colors.white
                        }}
                      >
                        📦 Receive
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Movements */}
      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📊</span>
            <span>Recent Stock Movements</span>
          </div>
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Date</th>
                <th style={styles.tableHeader}>Item</th>
                <th style={styles.tableHeader}>Type</th>
                <th style={styles.tableHeader}>Quantity</th>
                {!isMobile && <th style={styles.tableHeader}>Created By</th>}
              </tr>
            </thead>
            <tbody>
              {movements.slice(0, 10).map(movement => (
                <tr key={movement.id}>
                  <td style={styles.tableCell}>{formatDate(movement.movement_date)}</td>
                  <td style={styles.tableCell}>{movement.item_name}</td>
                  <td style={styles.tableCell}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: movement.movement_type === 'in' ? `${colors.success}20` : `${colors.warning}20`,
                      color: movement.movement_type === 'in' ? colors.success : colors.warning,
                      textTransform: 'uppercase'
                    }}>
                      {movement.movement_type}
                    </span>
                  </td>
                  <td style={styles.tableCell}>
                    <strong>{movement.quantity} {movement.unit}</strong>
                  </td>
                  {!isMobile && (
                    <td style={styles.tableCell}>{movement.created_by}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PO Modal */}
      {showPOModal && (
        <div style={styles.modal} onClick={() => setShowPOModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              Create Purchase Order
            </h3>

            <form onSubmit={handleCreatePO}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Supplier *</label>
                  <select
                    value={poForm.supplier_id}
                    onChange={(e) => setPOForm({ ...poForm, supplier_id: e.target.value })}
                    style={styles.select}
                    required
                  >
                    <option value="">Select supplier...</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.supplier_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Order Date *</label>
                  <input
                    type="date"
                    value={poForm.order_date}
                    onChange={(e) => setPOForm({ ...poForm, order_date: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Expected Delivery</label>
                <input
                  type="date"
                  value={poForm.expected_delivery}
                  onChange={(e) => setPOForm({ ...poForm, expected_delivery: e.target.value })}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Items *</label>
                {poForm.items.map((item, index) => (
                  <div key={index} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr 50px', gap: '8px', marginBottom: '8px' }}>
                    <select
                      value={item.item_id}
                      onChange={(e) => updatePOItem(index, 'item_id', e.target.value)}
                      style={{ ...styles.select, marginBottom: 0 }}
                      required
                    >
                      <option value="">Select item...</option>
                      {inventory.map(inv => (
                        <option key={inv.id} value={inv.id}>
                          {inv.item_name} ({inv.item_code})
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updatePOItem(index, 'quantity', e.target.value)}
                      style={styles.input}
                      required
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => updatePOItem(index, 'unit_price', e.target.value)}
                      style={styles.input}
                      required
                    />
                    <input
                      type="text"
                      value={`₹${item.total_price}`}
                      style={{ ...styles.input, background: colors.lightGray }}
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={() => removePOItem(index)}
                      style={{
                        ...styles.button,
                        background: colors.danger,
                        padding: '10px'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addPOItem}
                  style={{
                    ...styles.button,
                    background: colors.accent,
                    marginTop: '8px'
                  }}
                >
                  + Add Item
                </button>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea
                  value={poForm.notes}
                  onChange={(e) => setPOForm({ ...poForm, notes: e.target.value })}
                  style={{ ...styles.input, minHeight: '80px' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="submit" style={{ ...styles.button, flex: 1 }}>
                  Create Purchase Order
                </button>
                <button
                  type="button"
                  onClick={() => setShowPOModal(false)}
                  style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Movement Modal */}
      {showMovementModal && (
        <div style={styles.modal} onClick={() => setShowMovementModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              Record Stock Movement
            </h3>

            <form onSubmit={handleRecordMovement}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Item *</label>
                <select
                  value={movementForm.item_id}
                  onChange={(e) => setMovementForm({ ...movementForm, item_id: e.target.value })}
                  style={styles.select}
                  required
                >
                  <option value="">Select item...</option>
                  {inventory.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.item_name} ({item.item_code}) - Current: {item.current_stock} {item.unit}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Movement Type *</label>
                  <select
                    value={movementForm.movement_type}
                    onChange={(e) => setMovementForm({ ...movementForm, movement_type: e.target.value })}
                    style={styles.select}
                    required
                  >
                    <option value="in">Stock In</option>
                    <option value="out">Stock Out</option>
                    <option value="adjustment">Adjustment</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Quantity *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={movementForm.quantity}
                    onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea
                  value={movementForm.notes}
                  onChange={(e) => setMovementForm({ ...movementForm, notes: e.target.value })}
                  style={{ ...styles.input, minHeight: '80px' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="submit" style={{ ...styles.button, flex: 1 }}>
                  Record Movement
                </button>
                <button
                  type="button"
                  onClick={() => setShowMovementModal(false)}
                  style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PO Details Modal */}
      {showPODetails && selectedPO && (
        <div style={styles.modal} onClick={() => setShowPODetails(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              Purchase Order Details
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>
                {selectedPO.po_number}
              </div>
              <div style={{ fontSize: '14px', color: colors.textLight }}>
                {selectedPO.supplier_name}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '8px' }}>Items:</h4>
              {poItems.map(item => (
                <div key={item.id} style={{
                  padding: '10px',
                  background: colors.lightGray,
                  borderRadius: '6px',
                  marginBottom: '6px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600' }}>{item.item_name}</div>
                      <div style={{ fontSize: '11px', color: colors.textLight }}>
                        {item.quantity} {item.unit} × ₹{parseFloat(item.unit_price).toFixed(2)}
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: colors.primary }}>
                      ₹{parseFloat(item.total_price).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ borderTop: `2px solid ${colors.mediumGray}`, paddingTop: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: colors.primary }}>
                <span>Total Amount:</span>
                <span>₹{parseFloat(selectedPO.total_amount).toLocaleString('en-IN')}</span>
              </div>
            </div>

            <button
              onClick={() => setShowPODetails(false)}
              style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, width: '100%' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default StoresOperations;